import {
  Input,
  Table,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import TooltipWrapper from '@/components/ui/tooltip';
import useOpenInterest from '@/features/markets/api/get-open-interest';
import { useTicker24hr } from '@/features/markets/api/get-ticker-24hr';
import { useTickerLatest } from '@/features/markets/api/get-ticker-latest';
import { FavoriteButton } from '@/features/markets/components/favorite-button';
import { getMaxLeverage } from '@/features/trade/order/utils/math';
import { useFavorites } from '@/hooks';
import { cn } from '@/lib/utils';
import {
  FUNDING_RATE_DECIMALS,
  MarketStatus,
  useMarketStore,
  useMarketStores,
} from '@/store/use-markets-store';
import {
  abbrFromSym,
  imgFromAbbr,
  symbolToBaseQuote,
} from '@/utils/token-symbol';
import {
  bigIntToDecimalStr,
  bigIntToNum,
  formatBigInt,
  formatNumber,
} from '@/utils/value-format';
import { ColumnDef } from '@tanstack/react-table';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

interface MarketItem {
  symbol: string;
  leverage: string;
  markPrice: bigint;
  indexPrice: bigint;
  priceChange: bigint;
  priceChangePct: bigint;
  fundingRate: bigint;
  isFavorite: boolean;
  quoteVolume?: bigint;
  openInterest?: bigint;
  isolated: boolean;
  status: MarketStatus;
}

export const SYMBOL_CATEGORIES = {
  New: [
    'AUD-USD',
    'CAD-USD',
    'CHF-USD',
    'EUR-USD',
    'GBP-USD',
    'JPY-USD',
    'NOK-USD',
    'NZD-USD',
    'SEK-USD',
    'LAUNCHCOIN',
    'NXPC',
    'SOPH',
    'HYPE',
    'RESOLV',
  ],
  Dominance: ['BTC.D', 'OTHERS.D', 'STABLE.D'],
  Index: ['VC'],
  AI: [
    'AI16Z',
    'AIXBT',
    'AR',
    'ARC',
    'ARKM',
    'FET',
    'GRASS',
    'GRIFFAIN',
    'IO',
    'KAITO',
    'NEAR',
    'PROPMT',
    'RENDER',
    'TAO',
    'VANA',
    'VIRTUAL',
    'ZEREBRO',
  ],
  Meme: [
    'ALCH',
    'BRETT',
    'DOGE',
    'FARTCOIN',
    'kBONK',
    'kFLOKI',
    'kNEIRO',
    'kPEPE',
    'MELANIA',
    'MEME',
    'MEW',
    'NOT',
    'PENGU',
    'PNUT',
    'POPCAT',
    'SPX',
    'TRUMP',
    'TURBO',
    'TST',
    'WIF',
  ],
  Forex: [
    'AUD-USD',
    'CAD-USD',
    'CHF-USD',
    'EUR-USD',
    'GBP-USD',
    'JPY-USD',
    'NOK-USD',
    'NZD-USD',
    'SEK-USD',
  ],
};

export type SymbolCategory = keyof typeof SYMBOL_CATEGORIES;

const columns: ColumnDef<MarketItem>[] = [
  {
    accessorKey: 'symbol',
    header: 'Symbol',
    cell: ({ row }) => (
      <div className="flex h-full items-center gap-3">
        <div className="md:mr-1.5">
          <FavoriteButton symbol={row.original.symbol} size={14} />
        </div>
        <img
          src={imgFromAbbr(abbrFromSym(row.original.symbol))}
          alt="symbol"
          className="h-5 w-5"
        />
        <span className="hidden font-sans md:inline">
          {symbolToBaseQuote(row.original.symbol)}
        </span>
        <span className="font-sans md:hidden">
          {abbrFromSym(row.original.symbol)}
        </span>
        <div className="rounded bg-[#271714] px-2">
          <span className="font-mono text-sm text-primary">
            {formatNumber(row.original.leverage, {
              style: 'leverage',
            })}
          </span>
        </div>
        {row.original.isolated && (
          <TooltipWrapper
            content={
              <span>
                Backed by isolated liquidity pool. See{' '}
                <Link
                  className="underline"
                  target="_blank"
                  rel="noreferrer"
                  to="https://docs.vest.exchange/overview/traders/perpetual-contract-specifications"
                  onClick={(e) => e.stopPropagation()}
                >
                  Docs
                </Link>{' '}
                for details.
              </span>
            }
          >
            <div className="hidden rounded bg-[#271714] px-2 sm:block">
              <span className="font-mono text-xs uppercase leading-none text-primary sm:text-sm">
                Isolated
              </span>
            </div>
          </TooltipWrapper>
        )}
      </div>
    ),
    meta: {
      width: 'md:w-[35%] lg:w-[25%]',
    },
  },
  {
    accessorKey: 'markPrice',
    header: 'Mark Price',
    cell: ({ row }) => {
      const {
        marketSpec: { priceDecimals },
      } = useMarketStore(row.original.symbol).getState();
      return (
        <div className={`flex items-center`}>
          <span>{formatBigInt(row.original.markPrice, priceDecimals)}</span>
        </div>
      );
    },
    sortingFn: (a, b) => {
      const {
        marketSpec: { priceDecimals: aPriceDecimals },
      } = useMarketStore(a.original.symbol).getState();
      const {
        marketSpec: { priceDecimals: bPriceDecimals },
      } = useMarketStore(b.original.symbol).getState();
      return (
        bigIntToNum(a.original.markPrice, aPriceDecimals) -
        bigIntToNum(b.original.markPrice, bPriceDecimals)
      );
    },
  },
  {
    accessorKey: 'indexPrice',
    header: 'Index Price',
    cell: ({ row }) => {
      const {
        marketSpec: { priceDecimals },
      } = useMarketStore(row.original.symbol).getState();
      return (
        <div className={`flex items-center`}>
          <span>{formatBigInt(row.original.indexPrice, priceDecimals)}</span>
        </div>
      );
    },
    sortingFn: (a, b) => {
      const {
        marketSpec: { priceDecimals: aPriceDecimals },
      } = useMarketStore(a.original.symbol).getState();
      const {
        marketSpec: { priceDecimals: bPriceDecimals },
      } = useMarketStore(b.original.symbol).getState();
      return (
        bigIntToNum(a.original.indexPrice, aPriceDecimals) -
        bigIntToNum(b.original.indexPrice, bPriceDecimals)
      );
    },
    meta: {
      width: 'hidden sm:table-cell',
    },
  },
  {
    accessorKey: 'priceChange',
    header: () => (
      <div className="flex w-full justify-end md:justify-start">24h Change</div>
    ),
    cell: ({ row }) => {
      const {
        marketSpec: { priceDecimals },
      } = useMarketStore(row.original.symbol).getState();
      return (
        <div className="flex items-center max-md:justify-end">
          <span
            className={cn('text-right md:text-left', {
              'text-red': row.original.priceChange < 0,
              'text-green': row.original.priceChange >= 0,
            })}
          >
            {`${formatBigInt(row.original.priceChangePct, 4n, { style: 'percent', abs: true })} / ${formatBigInt(row.original.priceChange, priceDecimals, { showChange: true })}`}
          </span>
        </div>
      );
    },
    sortingFn: (a, b) =>
      Number(a.original.priceChangePct) - Number(b.original.priceChangePct),
    meta: {
      width: 'pr-6 md:pr-0',
    },
  },
  {
    accessorKey: 'fundingRate',
    header: '1h Funding',
    cell: ({ row }) => (
      <div className={`flex items-center`}>
        <span
          className={cn({
            'text-red': row.original.fundingRate < 0,
            'text-green': row.original.fundingRate >= 0,
          })}
        >
          {formatBigInt(row.original.fundingRate, FUNDING_RATE_DECIMALS, {
            style: 'percent',
            digits: Number(FUNDING_RATE_DECIMALS) - 2,
          })}
        </span>
      </div>
    ),
    meta: {
      width: 'hidden md:table-cell',
    },
  },
  {
    accessorKey: 'openInterest',
    header: 'Open Interest',
    cell: ({ row }) => {
      const {
        marketSpec: { sizeDecimals, priceDecimals },
      } = useMarketStore(row.original.symbol).getState();
      return typeof row.original.openInterest === 'bigint'
        ? formatBigInt(
            row.original.openInterest,
            priceDecimals + sizeDecimals,
            { digits: 2 },
          )
        : '-';
    },
    enableSorting: true,
    sortingFn: (a, b) => {
      const {
        marketSpec: {
          priceDecimals: aPriceDecimals,
          sizeDecimals: aSizeDecimals,
        },
      } = useMarketStore(a.original.symbol).getState();
      const {
        marketSpec: {
          priceDecimals: bPriceDecimals,
          sizeDecimals: bSizeDecimals,
        },
      } = useMarketStore(b.original.symbol).getState();
      return (
        (a.original.openInterest
          ? bigIntToNum(a.original.openInterest, aSizeDecimals + aPriceDecimals)
          : 0) -
        (b.original.openInterest
          ? bigIntToNum(b.original.openInterest, bSizeDecimals + bPriceDecimals)
          : 0)
      );
    },
    meta: {
      width: 'hidden md:table-cell md:pr-8 lg:pr-1',
    },
  },
  {
    accessorKey: 'volume',
    header: '24h Volume',
    cell: ({ row }) => {
      const {
        marketSpec: { sizeDecimals, priceDecimals },
      } = useMarketStore(row.original.symbol).getState();
      return typeof row.original.quoteVolume === 'bigint'
        ? formatBigInt(row.original.quoteVolume, priceDecimals + sizeDecimals, {
            digits: 2,
          })
        : '-';
    },
    sortingFn: (a, b) => {
      const {
        marketSpec: {
          priceDecimals: aPriceDecimals,
          sizeDecimals: aSizeDecimals,
        },
      } = useMarketStore(a.original.symbol).getState();
      const {
        marketSpec: {
          priceDecimals: bPriceDecimals,
          sizeDecimals: bSizeDecimals,
        },
      } = useMarketStore(b.original.symbol).getState();
      return (
        (a.original.quoteVolume
          ? bigIntToNum(a.original.quoteVolume, aSizeDecimals + aPriceDecimals)
          : 0) -
        (b.original.quoteVolume
          ? bigIntToNum(b.original.quoteVolume, bSizeDecimals + bPriceDecimals)
          : 0)
      );
    },
    meta: {
      width: 'hidden lg:table-cell',
    },
  },
];

export default function MarketsPage() {
  const navigate = useNavigate();
  const [tableTab, setTableTab] = useState<'all' | 'favorite' | SymbolCategory>(
    'all',
  );
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { isFavorite } = useFavorites();

  const markets = useMarketStores((state) => ({
    marketData: state.marketData,
    marketSpec: state.marketSpec,
  }));

  useTickerLatest({});
  useTicker24hr({});
  useOpenInterest({});

  const data = useMemo(() => {
    return Object.entries(markets)
      .filter(([symbol]) => {
        if (
          !abbrFromSym(symbol)
            .toLowerCase()
            .includes(debouncedSearch.toLowerCase())
        )
          return false;
        if (tableTab === 'all') return true;
        if (tableTab === 'favorite') return isFavorite(symbol);
        return SYMBOL_CATEGORIES[tableTab].includes(abbrFromSym(symbol));
      })
      .map(([symbol, market]) => ({
        symbol,
        leverage: getMaxLeverage(market.marketSpec)?.decimal,
        markPrice: market.marketData.markPrice,
        indexPrice: market.marketData.indexPrice,
        priceChange: market.marketData.priceChange,
        priceChangePct: market.marketData.priceChangePct,
        fundingRate: market.marketData.oneHrFundingRate * 100n,
        isFavorite: tableTab === 'favorite' || isFavorite(symbol),
        quoteVolume: market.marketData.quoteVolume,
        openInterest: market.marketData.openInterest,
        isolated: market.marketSpec.isolated,
        status: market.marketSpec.status,
      }))
      .sort((a, b) => {
        if (a.isFavorite && !b.isFavorite) return -1;
        if (!a.isFavorite && b.isFavorite) return 1;
        return a.symbol.localeCompare(b.symbol);
      }) as MarketItem[];
  }, [debouncedSearch, markets, tableTab]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 100);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  const categories = useMemo(() => {
    const symbols = Object.keys(markets);
    return Object.keys(SYMBOL_CATEGORIES).filter((category) =>
      symbols.some((symbol) =>
        SYMBOL_CATEGORIES[category as SymbolCategory].includes(
          abbrFromSym(symbol),
        ),
      ),
    );
  }, [markets]);

  // const favorite = data.filter((symbol) => symbol.isFavorite);

  return (
    <div className="flex h-full flex-col">
      <Tabs
        className="flex flex-col"
        value={tableTab}
        onValueChange={(value) => setTableTab(value as SymbolCategory)}
      >
        <TabsList className="flex grid-cols-5 flex-col-reverse items-stretch border-b border-border md:grid md:h-16">
          <div className="no-scrollbar flex overflow-x-auto overflow-y-hidden md:col-span-4">
            <TabsTrigger
              className="h-14 min-w-max border-t-0 border-b-transparent max-lg:px-6 md:h-full"
              value="all"
            >
              All markets
            </TabsTrigger>
            <TabsTrigger
              className="h-14 min-w-max border-t-0 border-b-transparent max-lg:px-6 md:h-full"
              value="favorite"
            >
              Favorites
            </TabsTrigger>
            {categories.map((category) => (
              <TabsTrigger
                className="h-14 min-w-max border-t-0 border-b-transparent max-lg:px-6 md:h-full"
                value={category}
                key={category}
              >
                {category}
              </TabsTrigger>
            ))}
          </div>
          <div className="relative flex h-14 items-center border-border max-md:border-b md:h-full md:border-l">
            <Search className="absolute left-6 text-foreground" size={16} />
            <input
              type="text"
              placeholder="Search"
              className="relative z-10 w-full rounded-none bg-transparent pl-16 font-mono text-foreground placeholder:uppercase focus:outline-none"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </TabsList>
      </Tabs>
      <div className="relative flex-1">
        {tableTab === 'favorite' && data.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-4">
            <p className="font-mono text-lg uppercase text-vestgrey-100">
              No favorites yet, go add some
            </p>
          </div>
        ) : (
          <div className="absolute inset-0 h-full w-full pb-12 [&_tr]:cursor-pointer">
            <Table<MarketItem>
              columns={columns}
              data={data}
              onRowClick={(row) => navigate(`/trade/${row.symbol}`)}
              isMarketPage
              className="md:[&_table]:table-auto"
              getRowClassName={(row) =>
                row.status === MarketStatus.HALT ? 'opacity-75' : 'opacity-100'
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}
